/**
 * Repubblica wishlist — device-only (localStorage).
 *
 * Single source of truth for saved products. Owns localStorage, exposes a small
 * API on window.Wishlist, and keeps every on-page heart toggle + the header count
 * in sync via a `wishlist:change` event. No other file should touch localStorage.
 *
 * Snapshot stored per item (captured by snippets/wishlist-button.liquid):
 *   { schemaVersion, id, handle, title, url, image, price, addable,
 *     requiresOptions, available, preorderMessage, variantId, addedAt }
 */
(function () {
  'use strict';

  var KEY = 'rp:wishlist';

  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return [];
    }
  }

  function write(items) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(items));
      return true;
    } catch (e) {
      return false;
    }
  }

  function emit() {
    var items = read();
    document.dispatchEvent(new CustomEvent('wishlist:change', {
      detail: { count: items.length, ids: items.map(function (i) { return String(i.id); }) }
    }));
  }

  function publishWishlist(saved, item, source) {
    var events = window.RepubblicaAnonymousEvents;
    if (!events || typeof events.publish !== 'function' || !item) return;
    events.publish(saved ? 'repubblica:wishlist_added' : 'repubblica:wishlist_removed', {
      product_id: String(item.id || ''),
      product_handle: String(item.handle || '').slice(0, 160),
      product_title: String(item.title || '').slice(0, 200),
      variant_id: String(item.variantId || ''),
      action: saved ? 'added' : 'removed',
      source: source || 'wishlist_toggle'
    });
  }

  var Wishlist = {
    getAll: read,
    count: function () { return read().length; },
    has: function (id) {
      id = String(id);
      return read().some(function (i) { return String(i.id) === id; });
    },
    remove: function (id) {
      id = String(id);
      var items = read();
      var removed = items.find(function (i) { return String(i.id) === id; });
      write(items.filter(function (i) { return String(i.id) !== id; }));
      emit();
      publishWishlist(false, removed, 'wishlist_page');
    },
    toggle: function (item) {
      if (!item || item.id == null) return false;
      var id = String(item.id);
      var items = read();
      var idx = items.findIndex(function (i) { return String(i.id) === id; });
      var saved;
      if (idx > -1) {
        items.splice(idx, 1);
        saved = false;
      } else {
        item.addedAt = Date.now();
        items.unshift(item);
        saved = true;
      }
      write(items);
      emit();
      publishWishlist(saved, item, 'wishlist_toggle');
      return saved;
    }
  };

  window.Wishlist = Wishlist;

  /* ---------- UI sync ---------- */

  function syncToggle(btn) {
    var saved = Wishlist.has(btn.getAttribute('data-wishlist-id'));
    btn.classList.toggle('is-saved', saved);
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    btn.setAttribute('aria-label', saved ? 'Remove from wishlist' : 'Add to wishlist');
    var label = btn.querySelector('.WishlistToggle__Label');
    if (label) label.textContent = saved ? 'Saved' : 'Add to wishlist';
  }

  function syncAllToggles() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(syncToggle);
  }

  function syncCount() {
    var n = Wishlist.count();
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = n > 99 ? '99+' : String(n);
      el.classList.toggle('Visible', n > 0);
    });
  }

  /* ---------- Toggle clicks (delegated) ---------- */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    var item;
    try {
      item = JSON.parse(btn.getAttribute('data-wishlist-item'));
    } catch (err) {
      item = null;
    }
    if (item) Wishlist.toggle(item);
  });

  /* ---------- Wishlist page ---------- */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function setCardStatus(status, message, isError) {
    window.clearTimeout(status._wishlistClearTimer);
    status.textContent = message || '';
    status.classList.toggle('WishlistCard__Status--error', Boolean(isError));
    status.setAttribute('role', isError ? 'alert' : 'status');

    if (message) {
      status._wishlistClearTimer = window.setTimeout(function () {
        if (!status.isConnected) return;
        status.textContent = '';
        status.classList.remove('WishlistCard__Status--error');
        status.setAttribute('role', 'status');
      }, isError ? 8000 : 5000);
    }
  }

  function announceProductAdded(item, cartItemKey) {
    return new Promise(function (resolve) {
      var completed = false;
      var completionTimeout;
      var onComplete = function () {
        if (completed) return;
        completed = true;
        window.clearTimeout(completionTimeout);
        resolve();
      };

      completionTimeout = window.setTimeout(onComplete, 10000);
      document.dispatchEvent(new CustomEvent('product:added', {
        detail: {
          variant: { id: item.variantId },
          quantity: 1,
          cartItemKey: cartItemKey,
          onComplete: onComplete
        }
      }));

      if (!document.querySelector('section-cart-drawer')) onComplete();
    });
  }

  function addWishlistItem(item, button, status) {
    if (!item.variantId || button.disabled) return;

    window.clearTimeout(button._wishlistResetTimer);
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Adding…';
    setCardStatus(status, 'Adding ' + (item.title || 'product') + ' to cart.', false);
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    var cartAddUrl = window.routes && window.routes.cartAddUrl
      ? window.routes.cartAddUrl + '.js'
      : '/cart/add.js';

    var cartPayload = { id: item.variantId, quantity: 1 };
    if (item.preorderMessage) {
      cartPayload.properties = { 'Pre-order': item.preorderMessage };
    }

    fetch(cartAddUrl, {
      body: JSON.stringify(cartPayload),
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    }).then(function (response) {
      return response.json().then(function (content) {
        if (!response.ok) {
          var error = new Error(content.description || content.message || 'Unable to add this product to the cart.');
          error.cartContent = content;
          throw error;
        }
        return content;
      });
    }).then(function (content) {
      button.textContent = 'Added ✓';
      setCardStatus(status, (item.title || 'Product') + ' added to cart.', false);
      return announceProductAdded(item, content.key);
    }).catch(function (error) {
      var content = error.cartContent || {};
      var description = typeof content.description === 'object'
        ? content.message
        : content.description;
      button.textContent = 'Try again';
      setCardStatus(status, description || error.message || 'Unable to add this product to the cart.', true);
    }).finally(function () {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      button._wishlistResetTimer = window.setTimeout(function () {
        if (button.isConnected) button.textContent = item.preorderMessage ? 'Pre-order' : 'Add to cart';
      }, 2500);
    });
  }

  function buildCard(item) {
    var li = el('li', 'WishlistCard');

    var imgLink = el('a', 'WishlistCard__Img');
    imgLink.href = item.url || '#';
    if (item.image) {
      var img = el('img');
      img.src = item.image;
      img.alt = item.title || '';
      img.loading = 'lazy';
      imgLink.appendChild(img);
    }
    li.appendChild(imgLink);

    var titleLink = el('a', 'WishlistCard__Title', item.title || '');
    titleLink.href = item.url || '#';
    li.appendChild(titleLink);

    // Price + remove (✕) on one row.
    var meta = el('div', 'WishlistCard__Meta');
    // Instant placeholder from the stored snapshot: bold amount + quiet unit
    // (/m², /sheet), matching the collection card.
    var priceEl = el('div', 'WishlistCard__Price');
    if (item.price) {
      priceEl.appendChild(el('span', 'WishlistCard__PriceAmount', item.price));
      if (item.unit) priceEl.appendChild(el('span', 'WishlistCard__PriceUnit', item.unit));
    }
    meta.appendChild(priceEl);
    // Then upgrade to the live tile-aware price. This corrects tiles saved before
    // the unit was stored (they render amount-only above) and keeps the figure
    // current. Guarded to our own fragment markup, so a redirect/error/full page
    // can never overwrite the placeholder. Silent fallback on any failure.
    if (item.handle) {
      fetch('/products/' + encodeURIComponent(item.handle) + '?view=wishlist-price', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (html) {
          if (!html) return;
          html = html.trim();
          if (html && html.indexOf('WishlistCard__PriceAmount') !== -1) {
            priceEl.innerHTML = html;
          }
        })
        .catch(function () {});
    }

    var removeBtn = el('button', 'WishlistCard__Remove');
    removeBtn.type = 'button';
    removeBtn.setAttribute('aria-label', 'Remove from wishlist');
    removeBtn.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>';
    removeBtn.addEventListener('click', function () { Wishlist.remove(item.id); });
    meta.appendChild(removeBtn);

    li.appendChild(meta);

    var status = el('p', 'WishlistCard__Status');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    if (item.schemaVersion >= 2 && item.addable === true && item.variantId) {
      var directActionLabel = item.preorderMessage ? 'Pre-order' : 'Add to cart';
      var directActionAria = item.preorderMessage
        ? 'Pre-order ' + (item.title || 'product')
        : 'Add ' + (item.title || 'product') + ' to cart';
      var addButton = el('button', 'WishlistCard__Action WishlistCard__Action--add', directActionLabel);
      addButton.type = 'button';
      addButton.setAttribute('aria-label', directActionAria);
      addButton.addEventListener('click', function () {
        addWishlistItem(item, addButton, status);
      });
      li.appendChild(addButton);
    } else {
      var requiresOptions = item.requiresOptions !== false || item.available !== false;
      var actionLabel = requiresOptions ? 'Choose options' : 'View product';
      var productLink = el('a', 'WishlistCard__Action WishlistCard__Action--choose', actionLabel);
      productLink.href = item.url || '#';
      productLink.setAttribute('aria-label', actionLabel + ' for ' + (item.title || 'product'));
      li.appendChild(productLink);
    }

    li.appendChild(status);
    return li;
  }

  function renderPage() {
    var list = document.querySelector('[data-wishlist-list]');
    if (!list) return;
    var emptyEl = document.querySelector('[data-wishlist-empty]');
    var items = Wishlist.getAll();

    list.innerHTML = '';
    if (!items.length) {
      if (emptyEl) emptyEl.hidden = false;
      list.hidden = true;
      return;
    }
    if (emptyEl) emptyEl.hidden = true;
    list.hidden = false;
    items.forEach(function (item) { list.appendChild(buildCard(item)); });
  }

  /* ---------- Init + change handling ---------- */

  document.addEventListener('wishlist:change', function () {
    syncAllToggles();
    syncCount();
    renderPage();
  });

  function init() {
    syncAllToggles();
    syncCount();
    renderPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
